/**
 * SsoConfig.js
 *
 * Settings page for the SynergenHR SSO integration.
 * Route: /settings/general/sso-config
 *
 * Admins configure:
 *  - Enable/disable SSO
 *  - SSO Shared Secret (encrypted at rest)
 *  - SynergenHR Frontend URL
 *  - SynergenHR API URL
 */
import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Divider,
  Typography,
  Alert,
  Spin,
  Row,
  Col,
  Tooltip,
  Tag,
} from 'antd';
import {
  SwapOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  KeyOutlined,
  LinkOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Password } = Input;

const API_BASE = process.env.REACT_APP_API_URL + 'v1/sso-config';

async function fetchConfig() {
  const token = localStorage.getItem('authToken');
  const res = await fetch(API_BASE, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

async function saveConfig(data) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(API_BASE, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

function generateSecret() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 64 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('');
}

const SsoConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    fetchConfig()
      .then((data) => {
        setConfig(data);
        form.setFieldsValue({
          enabled: data.enabled,
          synergenFrontendUrl: data.synergenFrontendUrl || '',
          synergenApiUrl: data.synergenApiUrl || '',
        });
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(values) {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const payload = {
        enabled: values.enabled,
        synergenFrontendUrl: values.synergenFrontendUrl,
        synergenApiUrl: values.synergenApiUrl,
      };
      // Only send secret if a new one was entered
      if (values.ssoSecret) {
        payload.ssoSecret = values.ssoSecret;
      }
      await saveConfig(payload);
      setSaveSuccess(true);
      // Refresh to show updated status
      const updated = await fetchConfig();
      setConfig(updated);
      form.setFieldValue('ssoSecret', ''); // clear secret field after save
    } catch (err) {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function handleGenerateSecret() {
    form.setFieldValue('ssoSecret', generateSecret());
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680 }}>
      <Card
        title={
          <Space>
            <SwapOutlined />
            <span>SynergenHR SSO Integration</span>
            {config?.enabled ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>Enabled</Tag>
            ) : (
              <Tag color="default" icon={<ExclamationCircleOutlined />}>Disabled</Tag>
            )}
          </Space>
        }
      >
        {saveSuccess && (
          <Alert
            message="Settings saved successfully."
            type="success"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            onClose={() => setSaveSuccess(false)}
          />
        )}
        {saveError && (
          <Alert
            message={saveError}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            onClose={() => setSaveError('')}
          />
        )}

        <Form form={form} layout="vertical" onFinish={handleSave}>

          {/* Enable toggle */}
          <Form.Item name="enabled" valuePropName="checked" label="Enable SSO Integration">
            <Switch />
          </Form.Item>

          <Divider />

          {/* SynergenHR URLs */}
          <Title level={5} style={{ marginBottom: 16 }}>
            <LinkOutlined style={{ marginRight: 6 }} />
            SynergenHR Connection
          </Title>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="synergenFrontendUrl"
                label="SynergenHR Frontend URL"
                extra="e.g. http://localhost:8000"
              >
                <Input placeholder="http://localhost:8000" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="synergenApiUrl"
                label="SynergenHR API URL"
                extra="Same as frontend for Django (no separate API port)"
              >
                <Input placeholder="http://localhost:8000" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Shared Secret */}
          <Title level={5} style={{ marginBottom: 8 }}>
            <KeyOutlined style={{ marginRight: 6 }} />
            Shared Secret
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
            A long random string shared with SynergenHR. Used to sign and verify SSO tokens.
            Paste the same value into SynergenHR → Settings → Integrations → SSO Shared Secret.
            {config?.secretConfigured && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                <CheckCircleOutlined /> Secret is configured
              </Tag>
            )}
          </Text>

          <Form.Item
            name="ssoSecret"
            label={config?.secretConfigured ? 'New Secret (leave blank to keep current)' : 'SSO Shared Secret'}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Password
                placeholder={config?.secretConfigured ? '••••••••••••••••' : 'Enter or generate a secret'}
                style={{ flex: 1 }}
              />
              <Tooltip title="Generate a random 64-character secret">
                <Button icon={<ReloadOutlined />} onClick={handleGenerateSecret}>
                  Generate
                </Button>
              </Tooltip>
            </Space.Compact>
          </Form.Item>

          <Divider />

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="How it works"
            description={
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                <li>Set the SynergenHR URLs and generate a secret above.</li>
                <li>Paste the same secret into SynergenHR → Settings → Integrations.</li>
                <li>Enable the integration on both sides.</li>
                <li>Users click the ⇄ SynergenHR button in the nav to link and switch accounts.</li>
              </ol>
            }
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save Settings
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default SsoConfig;
